import bodymovin from 'lottie-web/build/player/lottie_light.js';
import hotspotAnimation from '@/assets/animations/hotspot.json';
import { HotspotEvents, ManifestHandlerEvents, PerspectiveEvents } from '@/viewerExtensions/events';
import { ViewerEvents, worldToCanvas, BaseExtension } from '@immersive-tech/viewer-engine';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';

import type { Viewer, Animations, Extension, Nullable } from '@immersive-tech/viewer-engine';
import type { AnimationItem, AnimationDirection } from 'lottie-web';
import type { IManifest, IManifestHotspot, IManifestXYZPosition } from '@/types/IManifest';
import type { ManifestHandler, Perspectives } from '@/viewerExtensions';
import type { IExtensionHotspotPointerEnterEvent } from '@/types/events/IExtensionHotspotPointerEnterEvent';
import type { IExtensionHotspotPointerLeaveEvent } from '@/types/events/IExtensionHotspotPointerLeaveEvent';
import type { IExtensionHotspotClickedEvent } from '@/types/events/IExtensionHotspotClickedEvent';
import type { IExtensionManifestLoadedEvent } from '@/types/events/IExtensionManifestLoadedEvent';

export class Hotspots extends BaseExtension {
  protected hotspots: Map<string, HTMLDivElement[]>;
  protected animations: AnimationItem[];
  protected resizeObserver: ResizeObserver;
  protected sceneJson: IManifest | null = null;
  public currentLevelHotspots: string[] = [];
  public topLevelHotspots: string[] = [];

  constructor(viewer: Viewer) {
    super(viewer);

    this.hotspots = new Map<string, HTMLDivElement[]>();
    this.animations = [];

    this.viewer.addEventListener(ManifestHandlerEvents.MANIFEST_LOADED, this.manifestLoadedHandler.bind(this) as EventListener);
    this.viewer.addEventListener(PerspectiveEvents.RESET_VIEW, this.resetViewHandler.bind(this));
    this.viewer.addEventListener(HotspotEvents.HOTSPOT_CLICKED, this.hotspotClickedHandler.bind(this) as EventListener);
    this.viewer.addEventListener(HotspotEvents.HOTSPOT_POINTER_ENTER, this.hotspotAnimationHandler.bind(this) as EventListener);
    this.viewer.addEventListener(HotspotEvents.HOTSPOT_POINTER_LEAVE, this.hotspotAnimationHandler.bind(this) as EventListener);
    this.viewer.addEventListener(ViewerEvents.VIEWER_POST_RENDER, this.adjustHotspotPositionsForRender.bind(this));
    this.resizeObserver = new ResizeObserver(this.handleResize.bind(this));

    this.resizeObserver.observe(this.viewer.canvasElement as unknown as Element);
  }

  protected get ManifestHandlerExtension() {
    return this.viewer.extensions.get('ManifestHandler') as Extension<ManifestHandler>;
  }

  protected get AnimationsExtension() {
    return this.viewer.extensions.get('Animations') as Extension<Animations>;
  }

  protected get PerspectivesExtension() {
    return this.viewer.extensions.get('Perspectives') as Extension<Perspectives>;
  }

  public get isCurrentLevelTopLevel() {
    for (const currentLevelHotspot of this.currentLevelHotspots) {
      if (!this.topLevelHotspots.includes(currentLevelHotspot)) {
        return false;
      }
    }

    return true;
  }

  public getHotspotInfo(hotspotName: string) {
    return this.sceneJson?.hotspots[hotspotName];
  }

  protected handleResize() {
    this.viewer.renderNextFrame += 1;
  }

  protected resetViewHandler() {
    const fastRewindAnimations = this.sceneJson?.fastRewind ?? false;
    const { animationStates, animationActionFunctions } = this.AnimationsExtension;

    for (const [animationName, actionFunction] of animationActionFunctions) {
      if (animationStates.get(animationName)!.currentFrame !== 0) {
        actionFunction(fastRewindAnimations ? -Number.MAX_SAFE_INTEGER : -1);
      }
    }

    if (this.sceneJson) {
      this.hideAllHotspots();

      const hotspotEntries = Object.entries(this.sceneJson.hotspots);

      for (const [hotspotName, hotspotManifestEntry] of hotspotEntries) {
        if (this.topLevelHotspots.includes(hotspotManifestEntry.hotspotName)) {
          this.showHotspot(hotspotName);
        }
      }
    }
  }

  protected manifestLoadedHandler(event: CustomEvent<IExtensionManifestLoadedEvent>) {
    const { data: sceneJson } = event.detail;

    this.sceneJson = sceneJson;

    const hotspotValues = Object.values(this.sceneJson.hotspots);

    for (const hotspotManifestEntry of hotspotValues) {
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0; i < hotspotManifestEntry.position.length; ++i) {
        this.createHotspot(hotspotManifestEntry);
      }

      if (hotspotManifestEntry.startVisible === true) {
        this.currentLevelHotspots.push(hotspotManifestEntry.hotspotName);
        this.topLevelHotspots.push(hotspotManifestEntry.hotspotName);
      }
    }
  }

  protected hotspotAnimationHandler(event: CustomEvent<IExtensionHotspotPointerEnterEvent | IExtensionHotspotPointerLeaveEvent>) {
    let direction: AnimationDirection = 1;
    const { hotspotName } = event.detail;

    if (this.hotspots.has(hotspotName)) {
      const hotspotDivs = this.hotspots.get(hotspotName) ?? [];

      for (const hotspotDiv of hotspotDivs) {
        const animationIndex = Number(hotspotDiv.getAttribute('animationIndex'));
        const animation = this.animations[animationIndex] as Nullable<AnimationItem>;

        if (!animation) return;

        if ((event.type as HotspotEvents) === HotspotEvents.HOTSPOT_POINTER_LEAVE) {
          direction = -1;
        }

        animation.setDirection(direction);
        animation.play();
      }
    }
  }

  protected hotspotPointerEnterHandler(event: PointerEvent) {
    if (!event.currentTarget) return;

    const hotspotDiv = event.currentTarget as HTMLDivElement;

    const hotspotName = hotspotDiv.getAttribute('hotspotName');

    this.viewer.dispatchViewerEvent(HotspotEvents.HOTSPOT_POINTER_ENTER, {
      hotspotName,
      hotspotElement: hotspotDiv,
    });
  }

  protected hotspotPointerLeaveHandler(event: PointerEvent) {
    if (!event.currentTarget) return;

    const hotspotDiv = event.currentTarget as HTMLDivElement;
    const hotspotName = hotspotDiv.getAttribute('hotspotName');

    this.viewer.dispatchViewerEvent(HotspotEvents.HOTSPOT_POINTER_LEAVE, {
      hotspotName,
    });
  }

  protected hotspotPointerUpHandler(event: PointerEvent) {
    if (!event.currentTarget) return;

    const hotspotDiv = event.currentTarget as HTMLDivElement;
    const hotspotName = hotspotDiv.getAttribute('hotspotName');

    this.viewer.dispatchViewerEvent(HotspotEvents.HOTSPOT_CLICKED, {
      hotspotName,
    });
  }

  protected hotspotClickedHandler(event: CustomEvent<IExtensionHotspotClickedEvent>) {
    this.triggerHotspot(event.detail.hotspotName);

    const newCurrentLevelHotspots: string[] = [];

    for (const hotspot of this.sceneJson?.hotspots[event.detail.hotspotName].hotspotUpdates ?? []) {
      if (hotspot[1] === true) {
        newCurrentLevelHotspots.push(hotspot[0]);
      }
    }

    this.currentLevelHotspots = newCurrentLevelHotspots;
  }

  public showHotspot(hotspotName: string) {
    if (this.hotspots.has(hotspotName)) {
      for (const hotspotDiv of this.hotspots.get(hotspotName)!) {
        hotspotDiv.classList.remove('scale-0');
        hotspotDiv.classList.add('scale-1');
      }
    }

    this.viewer.dispatchViewerEvent(HotspotEvents.HOTSPOT_SHOW, {
      hotspotName,
    });
  }

  public hideHotspot(hotspotName: string) {
    if (this.hotspots.has(hotspotName)) {
      for (const hotspotDiv of this.hotspots.get(hotspotName)!) {
        hotspotDiv.classList.remove('scale-1');
        hotspotDiv.classList.add('scale-0');
      }
    }

    this.viewer.dispatchViewerEvent(HotspotEvents.HOTSPOT_HIDE, {
      hotspotName,
    });
  }

  public hideAllHotspots() {
    for (const hotspotName of this.hotspots.keys()) {
      this.hideHotspot(hotspotName);
    }
  }

  public showAllHotspots() {
    for (const hotspotName of this.hotspots.keys()) {
      this.showHotspot(hotspotName);
    }
  }

  public triggerHotspot(hotspotName: Nullable<string>) {
    if (!hotspotName || !this.sceneJson || !(hotspotName in this.sceneJson.hotspots)) return;

    const { perspective, animations, hotspotUpdates } = this.sceneJson.hotspots[hotspotName];

    for (const [hotspotIndentifier, hotspotStatus] of hotspotUpdates) {
      this[hotspotStatus === true ? 'showHotspot' : 'hideHotspot'](hotspotIndentifier);
    }

    if (perspective) {
      this.PerspectivesExtension.setPerspective(perspective);
    }

    for (const [animationName, animationScale] of animations) {
      try {
        const animationActionFunction = this.AnimationsExtension.animationActionFunctions.get(animationName);

        animationActionFunction?.(animationScale);
      } catch (err) {
        console.warn(`Animation ${animationName} does not exist`);
      }
    }

    if (this.hotspots.has(hotspotName)) {
      for (const hotspotElement of this.hotspots.get(hotspotName)!) {
        const animationIndex = Number(hotspotElement.getAttribute('animationIndex'));
        const animation = this.animations[animationIndex] as Nullable<AnimationItem>;

        animation?.setDirection(-1);
        animation?.play();
      }
    }
  }

  public adjustHotspotPositionsForRender() {
    const hotspotCoordinate = new Vector3();
    const canvasDomRect = this.viewer.canvasElement.getBoundingClientRect();

    for (const hotspotName of this.hotspots.keys()) {
      if (!this.sceneJson) return;

      const manifestHotspot = this.sceneJson.hotspots[hotspotName] as Nullable<IManifestHotspot>;
      const manifestHotspotPositions = manifestHotspot?.position;

      if (!manifestHotspot) return;
      if (!manifestHotspotPositions) return;

      for (let index = 0; index < manifestHotspotPositions.length; ++index) {
        const manifestHotspotPosition = manifestHotspotPositions[index];

        if (!this.viewer.scene || !this.viewer.camera) continue;

        hotspotCoordinate.set(manifestHotspotPosition.x, manifestHotspotPosition.y, manifestHotspotPosition.z);

        const projectedPosition = worldToCanvas(hotspotCoordinate, this.viewer.scene, this.viewer.camera, this.viewer.canvasElement);
        const hotspotDiv = this.hotspots.get(hotspotName)?.[index];
        const top = `${canvasDomRect.y + projectedPosition.y}px`;
        const left = `${canvasDomRect.x + projectedPosition.x}px`;

        if (hotspotDiv?.style.getPropertyValue('top') !== top) {
          hotspotDiv?.style.setProperty('top', top);
        }

        if (hotspotDiv?.style.getPropertyValue('left') !== left) {
          hotspotDiv?.style.setProperty('left', left);
        }
      }
    }
  }

  public moveHotspotTarget(hotspotName: string, targetIndex: number, position: IManifestXYZPosition) {
    const targetHotspot = this.hotspots.get(hotspotName);
    const targetHotspotDiv = targetHotspot?.[targetIndex];

    if (!targetHotspotDiv || !this.viewer.scene || !this.viewer.camera) return;

    const canvasDomRect = this.viewer.canvasElement.getBoundingClientRect();
    const hotspotCoordinate = new Vector3(position.x, position.y, position.z);
    const projectedPosition = worldToCanvas(hotspotCoordinate, this.viewer.scene, this.viewer.camera, this.viewer.canvasElement);

    targetHotspotDiv.style.setProperty('top', `${canvasDomRect.y + projectedPosition.y}px`);
    targetHotspotDiv.style.setProperty('left', `${canvasDomRect.x + projectedPosition.x}px`);

    this.viewer.renderNextFrame += 1;
  }

  public destroyHotspotTarget(hotspotName: string, targetIndex: number) {
    const targetHotspot = this.hotspots.get(hotspotName)!;
    const targetHotspotDiv = targetHotspot[targetIndex];
    const animationIndex = Number(targetHotspotDiv.getAttribute('animationIndex'));

    this.animations.splice(animationIndex, 1);
    targetHotspotDiv.remove();
    targetHotspot.splice(targetIndex, 1);

    this.viewer.dispatchViewerEvent(HotspotEvents.HOTSPOT_TARGET_DESTROYED, {
      hotspotName,
      target: targetIndex,
    });
  }

  public createHotspotTarget(target: IManifestXYZPosition, hotspotIdentifier: string) {
    const hotspotDiv = document.createElement('div');
    const animation = bodymovin.loadAnimation({
      container: hotspotDiv,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      name: `Hotspot ${hotspotIdentifier} Animation`,
      animationData: hotspotAnimation,
    });

    animation.setSpeed(3);

    hotspotDiv.classList.add('fixed', 'block', 'w-[64px]', 'h-[64px]', 'cursor-pointer', 'touch-none', 'select-none', 'no-underline', 'outline-0', 'scale-0', '-translate-x-1/2', '-translate-y-1/2', 'transition-transform', 'duration-500', 'ease-linear');

    hotspotDiv.addEventListener('pointerenter', this.hotspotPointerEnterHandler.bind(this));
    hotspotDiv.addEventListener('pointerleave', this.hotspotPointerLeaveHandler.bind(this));
    hotspotDiv.addEventListener('pointerup', this.hotspotPointerUpHandler.bind(this));

    hotspotDiv.setAttribute('animationIndex', Number(this.animations.push(animation) - 1).toString());
    hotspotDiv.setAttribute('hotspotName', hotspotIdentifier);

    if (!this.hotspots.has(hotspotIdentifier)) {
      this.hotspots.set(hotspotIdentifier, []);
    }

    this.hotspots.get(hotspotIdentifier)?.push(hotspotDiv);

    this.viewer.originalContainer.append(hotspotDiv);

    this.viewer.dispatchViewerEvent(HotspotEvents.HOTSPOT_TARGET_CREATED, {
      hotspotName: hotspotIdentifier,
      position: target,
    });
  }

  public createHotspotTargets(targets: IManifestXYZPosition[], hotspotIdentifier: string) {
    for (const target of targets) {
      this.createHotspotTarget(target, hotspotIdentifier);
    }
  }

  public createHotspot(hotspotInfo: IManifestHotspot) {
    if (!this.hotspots.has(hotspotInfo.hotspotName)) {
      this.hotspots.set(hotspotInfo.hotspotName, []);
    }

    this.createHotspotTargets(hotspotInfo.position, hotspotInfo.hotspotName);

    this.viewer.dispatchViewerEvent(HotspotEvents.HOTSPOT_CREATED, {
      hotspotName: hotspotInfo.hotspotName,
    });
  }

  public destroyHotspot(hotspotName: string) {
    if (this.hotspots.has(hotspotName)) {
      for (const hotspotDiv of this.hotspots.get(hotspotName)!) {
        const animationIndex = Number(hotspotDiv.getAttribute('animationIndex'));

        this.animations.splice(animationIndex, 1);

        hotspotDiv.remove();
      }
    }

    this.hotspots.delete(hotspotName);

    this.viewer.dispatchViewerEvent(HotspotEvents.HOTSPOT_DESTROYED, {
      hotspotName,
    });
  }

  public destroyAllHotspots() {
    for (const hotspotName of this.hotspots.keys()) {
      this.destroyHotspot(hotspotName);
    }
  }

  public destroyAllHotspotTargets(hotspotName: string) {
    const hotspot = this.hotspots.get(hotspotName);

    if (!hotspot || hotspot.length === 0) return;

    for (let index = hotspot.length - 1; index >= 0; --index) {
      this.destroyHotspotTarget(hotspotName, index);
    }
  }

  public renameHotspotTarget(oldName: string, newName: string) {
    this.hotspots.set(newName, this.hotspots.get(oldName)!);
    this.hotspots.delete(oldName);
  }
}
